import React from 'react';

type KoobCatalogMapLayoutProps = {
  /** The results column: cards, then pagination. Scrolls on its own. */
  list: React.ReactNode;
  /** The map, which fills its card. */
  map: React.ReactNode;
};

// Keeps the cards clear of the window's bottom edge.
const BOTTOM_GAP_PX = 16;

/**
 * Map mode of the hotel and experience catalogs, in DMC and TO: the results
 * column and the map side by side. It sits where the list grid sits, under the
 * same page header and filters, and fills the window down to its bottom edge.
 */
export default function KoobCatalogMapLayout({
  list,
  map,
}: KoobCatalogMapLayoutProps) {
  const [top, setTop] = React.useState(0);
  // ponytail: measured on mount; a filter row that wraps later leaves the
  // page a little taller than the window. Add a ResizeObserver if that shows.
  const measure = React.useCallback((node: HTMLDivElement | null) => {
    if (node) {
      setTop(Math.round(node.getBoundingClientRect().top + window.scrollY));
    }
  }, []);

  // From xl: list and map side by side, filling the window; wider list and
  // horizontal cards from 2xl. Below xl the map stacks on top at a fixed height
  // and the page scrolls, so the map never shrinks to a sliver.
  return (
    <div
      ref={measure}
      className="flex flex-col gap-4 xl:h-[var(--fill)] xl:min-h-[480px] xl:flex-row"
      style={
        {
          '--fill': `calc(100vh - ${top + BOTTOM_GAP_PX}px)`,
        } as React.CSSProperties
      }
    >
      {/* No card behind the cards, as in list view; the padding keeps their
          shadows from being clipped by the scroll box. */}
      <div className="p-1 xl:min-h-0 xl:w-[340px] xl:flex-none xl:overflow-y-auto 2xl:w-[560px]">
        {/* No set height here, so cards sized h-full keep their own height. */}
        <div className="flex flex-col gap-4">{list}</div>
      </div>
      <div className="relative order-first h-[360px] flex-none overflow-hidden rounded-2xl bg-white shadow-sm xl:order-none xl:h-auto xl:min-w-0 xl:flex-1">
        {map}
      </div>
    </div>
  );
}
